import {Action, ReducerBuilder} from "redux-ts";
import {Reducer} from "platform/redux/Reducer";
import {TSMap} from "typescript-map";
import Utils from "platform/util/Utils";
import {OnAccountChanged, OnLoggedOut} from "core/redux/auth/AuthReduxActions";
import {FilterDateState, FilterDateType, FilterReduxState, FilterSymbolState} from "core/redux/filter/FilterReduxState";
import {
    PortfolioDateFilterPayload,
    ResetFilter, ResetFilterPayload,
    SetFilterVisible, SetFilterVisiblePayload,
    SetPortfolioDateFilter, UpdatePortfolioDateFilter,
    SetSymbolFilterContinent, SetSymbolFilterContinentPayload,
    SetMarketsStatus, SetMarketsStatusPayload
} from "core/redux/filter/FilterReduxActions";
import {PortfolioType} from "enum/PortfolioType";
import {RouteType} from "core/router/Routes";
import DateTimeFormat from "core/format/DateTimeFormat";
import {SetPortfolioTab, SetPortfolioTabPayload} from "core/redux/portfolio/PortfolioReduxActions";
import * as moment from "moment-mini";
import Platform from "platform/Platform";
import {ServiceType} from "enum/ServiceType";
import FilterEngine from "core/engine/FilterEngine";

const initialState = (): FilterReduxState => {
    const defaultDateState: FilterDateState = {
        dateType: FilterDateType.All
    };

    const portfolioDateFilter: TSMap<PortfolioType, FilterDateState> =  new TSMap();
    portfolioDateFilter.set(PortfolioType.OpenTrades, {...defaultDateState});
    portfolioDateFilter.set(PortfolioType.EntryOrders, {...defaultDateState});
    portfolioDateFilter.set(PortfolioType.ClosedTrades, {...defaultDateState, ...{
            dateType: FilterDateType.Today
        }});
    portfolioDateFilter.set(PortfolioType.ActivityLog, {...defaultDateState, ...{
            dateType: FilterDateType.Today
        }});
    return {
        portfolioDateFilter,
        symbolFilter:  new TSMap()
    };
};

export default class FilterReducer extends Reducer<FilterReduxState> {

    private static _instance: FilterReducer;

    public static instance(): FilterReducer {
        return this._instance || (this._instance = new this());
    }

    private constructor() {
        super();
        const filterEngine: FilterEngine = Platform.engine(ServiceType.Filter);
        this._middlewareActions.set("@@router5/TRANSITION_START", filterEngine.onChangeRoute);
    }

    public get name(): string {
        return "filter";
    }

    protected setup(builder: ReducerBuilder<FilterReduxState>): void {
        builder
            .init(initialState())
            .handle(OnLoggedOut, (state: FilterReduxState, action: Action<any>) => {
                return {
                    ...initialState()
                };
            })
            .handle(OnAccountChanged, (state: FilterReduxState, action: Action<any>) => {
                return {
                    ...initialState()
                };
            })
            .handle(SetSymbolFilterContinent, (state: FilterReduxState, {payload}: Action<SetSymbolFilterContinentPayload>) => {
                const newState: FilterReduxState = Utils.merge({}, state);
                const {symbolsCategory, continent} = payload;
                const filter: FilterSymbolState = newState.symbolFilter.get(symbolsCategory) || {};
                const continentArray = filter.continent ? [...filter.continent] : [];
                const continentIndex = continentArray.indexOf(continent);

                if (continentIndex >= 0) {
                    continentArray.splice(continentIndex, 1);
                } else continentArray.push(continent);

                newState.symbolFilter.set(symbolsCategory, Object.assign({}, filter, {continent: continentArray}));
                return newState;
            })
            .handle(SetMarketsStatus, (state: FilterReduxState, {payload}: Action<SetMarketsStatusPayload>) => {
                const newState: FilterReduxState = Utils.merge({}, state);
                const {symbolsCategory, marketStatus} = payload;
                const filter: FilterSymbolState = newState.symbolFilter.get(symbolsCategory) || {};
                newState.symbolFilter.set(symbolsCategory, Object.assign({}, filter, {
                    marketStatus
                }));
                return newState;
            })
            .handle(SetFilterVisible, (state: FilterReduxState, {payload}: Action<SetFilterVisiblePayload>) => {
                const newState: FilterReduxState = Utils.merge({}, state);
                newState.filterVisible = payload.visible;
                if (payload.visible && payload.route === RouteType.MyTrades) {
                    const filter: FilterDateState = newState.portfolioDateFilter.get(payload.activePortfolioTab);
                    if (Utils.isNull(filter.from) || Utils.isNull(filter.to)) {
                        newState.portfolioDateFilter.set(payload.activePortfolioTab, Object.assign({}, filter, {
                            from: DateTimeFormat.now().startOf("day"),
                            to: DateTimeFormat.now().endOf("day")
                        }));
                    }
                }
                return newState;
            })
            .handle(SetPortfolioTab, (state: FilterReduxState, {payload}: Action<SetPortfolioTabPayload>) => {
                const filter: FilterDateState = state.portfolioDateFilter.get(payload.portfolioType);
                if (Utils.isNull(filter.from) || Utils.isNull(filter.to)) {
                    const newState: FilterReduxState = Utils.merge({}, state);
                    newState.portfolioDateFilter.set(payload.portfolioType, Object.assign({}, filter, {
                        from: DateTimeFormat.now().startOf("day"),
                        to: DateTimeFormat.now().endOf("day")
                    }));
                    return newState;
                }
                return state;
            })
            .handle(SetPortfolioDateFilter, (state: FilterReduxState, {payload}: Action<PortfolioDateFilterPayload>) => {
                return this.updatePortfolioFilter(state, payload.portfolioType, payload.dateType, payload.from, payload.to);
            })
            .handle(UpdatePortfolioDateFilter, (state: FilterReduxState, {payload}: Action<PortfolioDateFilterPayload>) => {
                return this.updatePortfolioFilter(state, payload.portfolioType, payload.dateType, payload.from, payload.to);
            })
            .handle(ResetFilter, (state: FilterReduxState, {payload}: Action<ResetFilterPayload>) => {
                const newState: FilterReduxState = Utils.merge({}, state);
                if (payload.route === RouteType.MyTrades) {
                    newState.portfolioDateFilter.set(payload.activePortfolioTab, Object.assign({}, newState.portfolioDateFilter.get(payload.activePortfolioTab), {
                        dateType: FilterDateType.All,
                        from: DateTimeFormat.now().startOf("day"),
                        to: DateTimeFormat.now().endOf("day")
                    }));
                } else if (payload.route === RouteType.Symbols) {
                    newState.symbolFilter.set(payload.activeSymbolsCategory, {
                        continent: []
                    });
                }
                return newState;
            });
    }

    private updatePortfolioFilter(state: FilterReduxState, portfolioType: PortfolioType, dateType: FilterDateType, from: moment.Moment, to: moment.Moment): FilterReduxState {
        const newState: FilterReduxState = Utils.merge({}, state);
        const filterState: FilterDateState = Object.assign({}, newState.portfolioDateFilter.get(portfolioType), {
            dateType
        });
        if (from && to) {
            filterState.from = from.startOf("day");
            filterState.to = to.endOf("day");
            this._logger.debug("Set portfolio date filter for " + portfolioType + ". From: " + filterState.from + " To: " + filterState.to);
        }
        newState.portfolioDateFilter.set(portfolioType, filterState);
        return newState;
    }
}
